import { useMemo } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from '@tanstack/react-router';
import { FilterableSelect } from '../common/FilterableSelect';
import { useProject, type BookSummaryDto } from '../../api/queries/projects';

interface DraftPickerProps {
  projectId: string;
  currentBookId: string;
}

export function DraftPicker({ projectId, currentBookId }: DraftPickerProps) {
  const navigate = useNavigate();
  const { data: project } = useProject(projectId);

  const books = project?.books ?? [];

  const options = useMemo(
    () =>
      books.map((b: BookSummaryDto) => ({
        value: b.id,
        label: `${b.isPrimary ? '\u2605 ' : ''}${b.label || `Draft ${b.versionNumber}`}`,
      })),
    [books],
  );

  const currentBook = books.find((b: BookSummaryDto) => b.id === currentBookId);

  const sourceOutlineLabel = useMemo(() => {
    if (!currentBook?.sourceOutlineId || !project?.outlines) return null;
    const outline = project.outlines.find(
      (o) => o.id === currentBook.sourceOutlineId,
    );
    return outline
      ? `${outline.label || `v${outline.versionNumber}`}${outline.sourceModel ? ` (${outline.sourceModel})` : ''}`
      : null;
  }, [currentBook, project?.outlines]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        px: 2,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Button
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() =>
          navigate({ to: '/projects/$projectId', params: { projectId } })
        }
      >
        Project
      </Button>

      <Box sx={{ minWidth: 260 }}>
        <FilterableSelect
          label="Draft"
          size="small"
          value={currentBookId}
          onChange={(newValue) => {
            if (newValue && newValue !== currentBookId) {
              navigate({
                to: '/books/$bookId/resume',
                params: { bookId: newValue },
              });
            }
          }}
          options={options}
          allowEmpty={false}
        />
      </Box>

      {currentBook?.stage && (
        <Chip size="small" label={currentBook.stage} variant="outlined" />
      )}

      {sourceOutlineLabel && (
        <Typography variant="caption" color="text.secondary">
          from: {sourceOutlineLabel}
        </Typography>
      )}
    </Stack>
  );
}
